import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import * as THREE from 'three';
import { DEFAULT_TABLE_THEME, type TableTheme } from './themes.js';

/**
 * A simple slab that sits under the chess board, so the scene reads as "board on
 * a table" rather than "board floating in a dark void". The board's squares span
 * 8 units (`squareToVec3` maps a1..h8 into [-3.5, 3.5]); this table is 13 units
 * wide (2.5 units of overhang on each side), 0.4 units thick, and sits just
 * below the board (board squares top at y=0; table top at y=-0.4).
 *
 * The material is derived from a `TableTheme` registry entry
 * (see `TABLE_THEMES` in `./themes.js`); the app passes the player's chosen
 * material × tone combination. Defaults to `wood-light` so the table is visible
 * even when no theme has been picked yet.
 */
export function Table({ theme = DEFAULT_TABLE_THEME }: { theme?: TableTheme }) {
  const geometry = useMemo(() => new THREE.BoxGeometry(13, 0.4, 13), []);
  // Recomputed on theme change — the slab is a single mesh, the geometry is
  // shared, only the material has to track the theme.
  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: theme.color,
        roughness: theme.roughness,
        metalness: theme.metalness,
      }),
    [theme],
  );
  // Centered vertically so the slab spans y = -0.6 to y = -0.2. Board squares
  // sit at y = -0.075 with top at y = 0, so there is a 0.2-unit air gap between
  // the top of the table and the bottom of the squares; the board reads as
  // resting on the table without z-fighting on its underside.
  return <mesh geometry={geometry} material={material} position={[0, -0.4, 0]} />;
}
